import { Fragment, useEffect } from "react";
import { H1, H2, Spinner, StackLayout, Text } from "@salt-ds/core";
import NotificationBanner from "src/app/NewNotificationBanner";
import { StepTracker } from "src/components/StepTracker";
import AchIntroduction from "./achIntroduction";
import useAchEnrollmentViewModel from "./useAchEnrollmentViewModel";
import { SessionWarningDialog } from "src/pages/achEnrollment/ach-session-timeout/SessionWarningDialog";
import {
  ACH_ENROLLMENT_HEADER_TXT,
  STEP_TRACKER_DEFAULT_STATUS,
} from "src/constants/achEnrollment";

const AchEnrollmentView = () => {
  const viewModel = useAchEnrollmentViewModel();

  // Scroll to the top when the spinner is shown
  useEffect(() => {
    if (viewModel.showSpinner) {
      window.scrollTo(0, 0);
    }
  }, [viewModel.showSpinner]);

  useEffect(() => {
    if (
      !viewModel.isAchQuestionsSubmitted &&
      !viewModel.isEmailAsDefaultLanding
    ) {
      viewModel.setIsAchIntroduction(true);
    } else {
      viewModel.setIsAchIntroduction(false);
    }
  }, [viewModel.isAchQuestionsSubmitted, viewModel.isEmailAsDefaultLanding]);

  // -- common renders -- //
  const renderNotificationBanner = () => {
    const {
      notificationBannerInfo: { message, type },
      showNotificationBanner,
      closeNotificationBanner,
    } = viewModel;

    if (!showNotificationBanner) {
      return null;
    }

    return (
      <NotificationBanner
        message={message}
        type={type}
        onClose={closeNotificationBanner}
      />
    );
  };

  const renderAchHeadings = () => {
    const { stepHeader, stepNames, activeStep } = viewModel;
    const style = {
      fontWeight: "var(--salt-palette-text-heading-fontWeight-heading)",
    };

    return (
      <Fragment>
        <H1 style={style}>{ACH_ENROLLMENT_HEADER_TXT}</H1>
        <H2>{`Step ${activeStep + 1} of ${stepNames.length}: ${stepHeader} `}</H2>
      </Fragment>
    );
  };

  const renderStepTracker = () => {
    const { activeStep, stepNames, stepStatusArray } = viewModel;

    return (
      <StepTracker
        activeStep={activeStep}
        stepNames={stepNames}
        stepStatusArray={stepStatusArray || STEP_TRACKER_DEFAULT_STATUS}
      />
    );
  };

  const renderSpinner = () => {
    const { showSpinner, spinnerInfoText } = viewModel;

    if (!showSpinner) {
      return null;
    }

    return (
      <StackLayout align="center">
        <Spinner size="large" aria-label="loading" role="status" />
        <H2>{spinnerInfoText}</H2>
      </StackLayout>
    );
  };

  const renderCommonComponents = () => {
    const { showSpinner } = viewModel;

    if (showSpinner) {
      return null;
    }

    return (
      <Fragment>
        {renderNotificationBanner()}
        {renderAchHeadings()}
        {renderStepTracker()}
      </Fragment>
    );
  };

  const renderDynamicComponents = () => {
    const {
      DynamicComponent,
      displayNotifications,
      closeNotificationBanner,
      setShowSpinner,
      showSpinner,
    } = viewModel;

    if (!DynamicComponent) {
      return null;
    }

    const model = {
      displayNotifications,
      closeNotificationBanner,
      setShowSpinner,
      showSpinner,
    };

    return <DynamicComponent viewModel={model} />;
  };

  // -- main Layout -- //
  const renderAchEnrollmentLayout = () => {
    const { isAchQuestionsSubmitted, isEmailAsDefaultLanding } = viewModel;

    if (!isEmailAsDefaultLanding && !isAchQuestionsSubmitted) {
      return <AchIntroduction viewModel={viewModel} />;
    }

    return (
      <div className="flex flex-col min-h-[80%] items-center">
        <div className="p-10 relative flex-col gap-4 inline-flex min-w-[684px]">
          {renderSpinner()}
          {renderCommonComponents()}
          {renderDynamicComponents()}
        </div>
      </div>
    );
  };

  return (
    <>
      {renderAchEnrollmentLayout()}
      {viewModel.showWarning &&
        !viewModel.isAchIntroduction && (
          <SessionWarningDialog
            onContinue={viewModel.handleContinue}
            onTimeout={viewModel.handleTimeout}
          />
        )}
    </>
  );
};

export default AchEnrollmentView;